/**
 * Where the browser opens an editing socket, and under what name (§8.3).
 *
 * **The handshake carries no token.** The browser `WebSocket` API cannot set
 * headers, so the session travels the only way it can — as the cookie the
 * handshake sends automatically — and `apps/collab/src/authenticate.ts` reads
 * it with the API's own `loadSession`. That works because collab shares an
 * origin with this app: `.do/app.*.yaml` routes `/collab` to the collab
 * component, and the Vite dev proxy reproduces that locally. **A collab server
 * on its own hostname would get no cookie at all**, and every connection would
 * be refused as `unauthenticated` with nothing in the client to explain why.
 * That is the whole reason the default below is a same-origin path rather than
 * `localhost:3001`, even though the service does listen on 3001.
 *
 * ## What the socket is authorized for
 *
 * `document.edit`, not `document.view` (`COLLAB_CAPABILITY`). A Yjs connection
 * is a write channel; readers in 1a render the published version over REST and
 * never open one. So a reader-only screen must not construct a provider — the
 * connection will be refused, correctly, and the refusal is not a bug to work
 * around. `DocumentResponse.viewer.seesWorkingDraft` is the flag that says
 * whether this person is an editor; gate the provider on it.
 *
 * ## Building the editor against this
 *
 * Two things are easy to get wrong and neither fails loudly:
 *
 * - **The extension list is `documentExtensions` from
 *   `@matchbox/shared/markdown/schema`.** It is the single definition of §5.1's
 *   closed component set, shared by the serializer and this editor so that a
 *   component existing in one and not the other cannot happen — and it already
 *   includes `UniqueID`, which is what makes block ids stable. `StarterKit`
 *   is *not* a substitute: it redefines `document`, `paragraph`, `text` and a
 *   dozen more, which throws on duplicate extension names, and the ones it adds
 *   beyond the closed set have no markdown spelling, so storing them would make
 *   the derived column a lie.
 * - **Undo/redo comes from Yjs, not from Tiptap.** `Collaboration` installs its
 *   own `UndoManager`; leaving Tiptap's history extension enabled alongside it
 *   gives one document two undo stacks that disagree about what the other
 *   editor just typed.
 */

/** The ingress prefix that reaches the collab component, in every environment. */
export const COLLAB_PATH = '/collab';

/**
 * Mirrors `DOCUMENT_NAME_PREFIX` in `apps/collab/src/documents.ts`.
 *
 * The mapping between this string and a `node.id` is a protocol, not a
 * convenience: Hocuspocus keys every in-memory `Y.Doc` by the name the client
 * sent, so two clients opening the same document under two names get two
 * separate documents with two separate persistence streams — a split brain that
 * reads as "my co-author's edits never arrive" and is invisible in the database.
 */
export const DOCUMENT_NAME_PREFIX = 'document:';

/**
 * `VITE_COLLAB_URL` is the escape hatch for a split-origin deployment, exactly
 * as `VITE_API_URL` is in `client.ts`. Setting it is also how you find out
 * whether the cookie still reaches the socket, so it is empty everywhere the
 * app is actually deployed.
 */
const CONFIGURED = String(import.meta.env.VITE_COLLAB_URL ?? '').replace(/\/+$/, '');

/**
 * The document name a node is edited under.
 *
 * Lower-cased here, and this is load-bearing. `nodeIdFromDocumentName` accepts
 * exactly one spelling — lower-case — and refuses the upper-case one, because
 * Postgres compares UUIDs canonically (`…ABC` and `…abc` are one row) while
 * Hocuspocus's document map is a plain string map (they would be two `Y.Doc`s
 * writing to that one row). The server cannot normalise, because the map is
 * keyed before any hook runs; the client can, and doing it at the single point
 * where every client builds the name is what makes "one node, one document"
 * true. A node id that arrives capitalised — from a hand-typed URL — would
 * otherwise be refused as `invalid-document-name` with no hint why.
 */
export function collabDocumentName(nodeId: string): string {
  return `${DOCUMENT_NAME_PREFIX}${nodeId.toLowerCase()}`;
}

/**
 * The socket endpoint. Not per-node: the document name travels *inside* the
 * protocol as a var-string, so every document on this origin shares one URL and
 * `HocuspocusProvider` takes the pair separately.
 */
export function collabSocketUrl(): string {
  if (CONFIGURED) return asWebSocketUrl(CONFIGURED);
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}${COLLAB_PATH}`;
}

/**
 * Everything `new HocuspocusProvider({ … })` needs to address one document —
 * the endpoint and the name, which are two fields and not one path.
 *
 * ```ts
 * const provider = new HocuspocusProvider({ ...collabConnection(nodeId), document: ydoc });
 * ```
 */
export function collabConnection(nodeId: string): { url: string; name: string } {
  return { url: collabSocketUrl(), name: collabDocumentName(nodeId) };
}

/**
 * The three refusals `onAuthenticate` can send, as
 * `apps/collab/src/authenticate.ts` defines them.
 *
 * Only `unauthenticated` is worth branching on: the correct response to it is
 * to send the person to sign in rather than to retry. The other two collapse a
 * missing node, a non-document node, and a genuine denial into one answer on
 * purpose — telling them apart is how an unauthenticated socket enumerates the
 * tree — so a client cannot say more than "you cannot edit this" and should
 * not try.
 */
export type CollabDenialReason = 'invalid-document-name' | 'unauthenticated' | 'forbidden';

/** Whether a refusal means "sign in again" rather than "you may not edit this". */
export function isAuthenticationDenial(reason: string | null | undefined): boolean {
  return reason === 'unauthenticated';
}

/** Accepts an `http(s)` or `ws(s)` origin and returns the `ws(s)` spelling. */
function asWebSocketUrl(url: string): string {
  if (url.startsWith('http://')) return `ws://${url.slice('http://'.length)}`;
  if (url.startsWith('https://')) return `wss://${url.slice('https://'.length)}`;
  return url;
}
